import { useState, useEffect } from 'react';
import { Outlet, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bars3Icon } from '@heroicons/react/24/outline';
import { Activity } from 'lucide-react';
import Sidebar from './Sidebar';
import { useScrollDirection } from '@/hooks/useScrollDirection';

const Layout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Scroll-to-hide header on mobile
  const headerVisible = useScrollDirection({ threshold: 15 });

  // Sidebar expanded state with localStorage persistence
  const [isExpanded, setIsExpanded] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sidebar-expanded');
      return saved ? JSON.parse(saved) : true; // Default expanded on desktop
    }
    return true;
  });

  // Toggle sidebar expanded state
  const toggleExpanded = () => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    localStorage.setItem('sidebar-expanded', JSON.stringify(newExpanded));
  };

  // Initialize sidebar state based on screen size
  useEffect(() => {
    const initializeSidebar = () => {
      const isMobileView = window.innerWidth < 768;
      setIsMobile(isMobileView);
      // Desktop: sidebar open by default, Mobile: sidebar closed by default
      setSidebarOpen(!isMobileView);
    };

    initializeSidebar();
  }, []);

  // Handle responsive behavior
  useEffect(() => {
    const checkMobile = () => {
      const isMobileView = window.innerWidth < 768;
      const wasMobile = isMobile;
      setIsMobile(isMobileView);

      // Handle transition between mobile and desktop
      if (wasMobile !== isMobileView) {
        if (isMobileView) {
          // Switching to mobile: close sidebar
          setSidebarOpen(false);
        } else {
          // Switching to desktop: open sidebar
          setSidebarOpen(true);
        }
      }
    };

    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [isMobile]);

  // Close sidebar when clicking outside on mobile
  const handleBackdropClick = () => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--gradient-clinical-bg)' }}>
      {/* Mobile Header - Only visible on mobile, hides on scroll down */}
      {isMobile && (
        <motion.header
          className="fixed top-0 left-0 right-0 h-14 bg-white border-b border-gray-200 flex items-center justify-between px-4 z-40 safe-top"
          initial={{ y: 0 }}
          animate={{ y: headerVisible ? 0 : -56 }}
          transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
        >
          {/* Logo - Clickable to go to dashboard */}
          <Link to="/dashboard" className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1e4a7a] to-[#16a085] flex items-center justify-center shadow-sm">
              <Activity className="text-white" size={16} strokeWidth={2.5} />
            </div>
            <div className="flex flex-col">
              <span className="text-[14px] font-semibold text-gray-900 leading-tight">Amplify</span>
              <span className="text-[9px] text-gray-500 leading-tight">by CareTracker</span>
            </div>
          </Link>

          {/* Menu Button - 44px touch target for Apple HIG compliance */}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="min-w-11 min-h-11 rounded-lg flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            aria-label={sidebarOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={sidebarOpen}
          >
            <Bars3Icon className="w-6 h-6" />
          </button>
        </motion.header>
      )}

      {/* Main Layout Container - padding transitions with header visibility */}
      <div className={isMobile ? `transition-[padding] duration-250 ease-in-out ${headerVisible ? 'pt-14' : 'pt-0'}` : ''}>
        <div className="flex relative min-h-screen">
          {/* Mobile Backdrop */}
          <AnimatePresence>
            {isMobile && sidebarOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                onClick={handleBackdropClick}
              />
            )}
          </AnimatePresence>

          {/* Sidebar */}
          <Sidebar
            isOpen={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            isMobile={isMobile}
            isExpanded={isExpanded}
            onToggleExpand={toggleExpanded}
          />

          {/* Main Content */}
          <main className={`
            flex-1 transition-all duration-300 ease-in-out relative min-h-screen
            overflow-x-hidden w-full
            ${isMobile
              ? 'p-4 max-w-full'
              : sidebarOpen
                ? (isExpanded ? 'ml-60 p-6' : 'ml-16 p-6')
                : 'ml-0 p-6'
            }
          `}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: 'easeOut' }}
              className="h-full w-full overflow-x-hidden"
            >
              <Outlet />
            </motion.div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default Layout;
